import log from "loglevel";
import {ConflictError, ConversionError, ExposedError, TrackedError, ValidationError,} from "@/types/Errors";
import {toast} from "react-hot-toast";

export type InterceptorName =
  | "redirect"
  | "notFound"
  | "validationError"
  | "auth"
  | "subscriptionPlan"
  | "duplicateError"
  | "forbidden"
  | "badRequest"
  | "methodNotAllowed"
  | "serverError"
  | "tooManyRequests"
  | "notAcceptable";

// type of the interceptor function
export type Interceptor = (error: any) => Promise<any>;

// 301, 307
export const redirectInterceptor = (error: any) => {
  if (error.response.status === 301 || error.response.status === 307) {
    log.debug("Redirecting to", error.response.data);
    const location = error.response.headers.get("Location");
    return Promise.reject({redirect: location});
  }
  return Promise.resolve();
};

// 404
export const notFoundInterceptor = (error: any) => {
  if (error.response.status === 404) {
    const err = new ExposedError(error.response.data);

    return Promise.reject(err);
  }

  return Promise.resolve();
};

// 422
export const validationErrorInterceptor = (error: any) => {
  if (error.response.status === 422) {
    return Promise.reject(new ValidationError(error.response.data));
  }

  return Promise.resolve();
};

// 401
export const authInterceptor = (error: any) => {
  if (error.response.status === 401) {
    return Promise.reject({redirect: "/login"});
  }

  return Promise.resolve();
};

// 402
export const subscriptionPlanInterceptor = (error: any) => {
  if (error.response.status === 402) {
    const err = new ExposedError(error.response.data);

    toast.error(err.fullMessage);
  }

  return Promise.resolve();
};

// 409
export const duplicateErrorInterceptor = (error: any) => {
  if (error.response.status === 409) {
    return Promise.reject(new ConflictError(error.response.data));
  }

  return Promise.resolve();
};

// 403
export const forbiddenInterceptor = (error: any) => {
  if (error.response.status === 403) {
    const err = new ExposedError(error.response.data);
    if (err.code === "Readonly") {
      toast.error(
        err.fullMessage
      );
    } else {
      toast.error(
        err.fullMessage
      );
    }

    return Promise.reject(error);
  }

  return Promise.resolve();
};

// 400
export const badRequestInterceptor = (error: any) => {
  if (error.response.status === 400) {
    const err = new ExposedError(error.response.data);
    if (err.code === "Conversion") {
      return Promise.reject(new ConversionError(error.response.data));
    } else {
      toast.error(
        err.fullMessage
      );
    }
  }

  return Promise.resolve();
};

// 405
export const methodNotAllowedInterceptor = (error: any) => {
  if (error.response.status === 405) {
    const err = new ExposedError(error.response.data);
    toast.error(
      err.fullMessage
    );
  }

  return Promise.resolve();
};

// 406
export const notAcceptableInterceptor = (error: any) => {
  if (error.response.status === 406) {
    const err = new ExposedError(error.response.data);
    if (err.code === "InvalidAPIVersion") {
      return Promise.reject({redirect: "/version_mismatch"});
    } else if (err.code === "UnverifiedAccount") {
      return Promise.reject({redirect: "/validate"});
    } else if (err.code === "NotOnboardedAccount") {
      return Promise.reject({redirect: "/onboarding"});
    } else {
      toast.error(
        err.fullMessage
      );
    }
  }

  return Promise.resolve();
};

// 500
export const serverErrorInterceptor = (error: any) => {
  if (error.response.status === 500) {
    const err = new TrackedError(error.response.data);
    log.error("Server error", err);

    toast.error(
      err.tracking_code ?
        `An error occurred on the server. Use the tracking code ${err.tracking_code} when contacting support`
        : "An error occurred on the server. Please contact support"
    );
  }

  return Promise.resolve();
};

// 429
export const tooManyRequestsInterceptor = (error: any) => {
  if (error.response.status === 429) {
    log.error("Too many requests");
    toast.error(
      "You have made too many requests in a short time",
    );
  }

  return Promise.resolve();
};

export const Interceptors: Record<InterceptorName, Interceptor> = {
  redirect: redirectInterceptor,
  notFound: notFoundInterceptor,
  validationError: validationErrorInterceptor,
  auth: authInterceptor,
  subscriptionPlan: subscriptionPlanInterceptor,
  duplicateError: duplicateErrorInterceptor,
  forbidden: forbiddenInterceptor,
  badRequest: badRequestInterceptor,
  methodNotAllowed: methodNotAllowedInterceptor,
  serverError: serverErrorInterceptor,
  tooManyRequests: tooManyRequestsInterceptor,
  notAcceptable: notAcceptableInterceptor,
};

export const allInterceptors = () => {
  return Object.keys(Interceptors).map((key) => key as InterceptorName);
};

export const excludeInterceptors = (exclude: InterceptorName[]) => {
  return Object.keys(Interceptors).filter(
    (key) => !exclude.includes(key as InterceptorName),
  ) as InterceptorName[];
};
